import { type UIMessage, useChat } from "@ai-sdk/react";
import { useAuth0 } from "@auth0/auth0-react";
import { DefaultChatTransport } from "ai";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useChatActions } from "../../contexts/chat-actions-context";
import type { User } from "../../hooks/useUser";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import { uploadFilesToBlob } from "../../services/fileUploadService";
import styles from "./ChatContainer.module.css";
import { ChatInput } from "./ChatInput";
import { ANALYTICS_EVENTS } from "./constants";
import { MessageList } from "./MessageList";
import { PersonalizedGreeting } from "./PersonalizedGreeting";

interface ChatContainerProps {
	id: string;
	user: User;
	initialMessages?: Array<{
		id: string;
		role: string;
		content: string | Array<{ text?: string }>;
		createdAt?: Date | string;
	}>;
	onFocus?: () => void;
	onBlur?: () => void;
	isExpanded?: boolean;
	onMessagesChange?: (messageCount: number) => void;
	chatState?: "compact" | "expandedInput" | "active";
	actionRow?: React.ReactNode;
	onScrollToBottomRef?: (scrollFn: () => void) => void;
	onSuggestClick?: () => void;
}

type MessagePart =
	| { type: "text"; text: string }
	| { type: "file"; url: string; mediaType: string };

function buildMessageParts(
	text: string,
	files?: Array<{ url: string; mediaType: string }>,
	metadata?: Record<string, unknown>,
): MessagePart[] {
	const parts: MessagePart[] = [
		{ type: "text", text, ...metadata } as MessagePart,
	];
	if (files) {
		for (const file of files) {
			parts.push({ type: "file", url: file.url, mediaType: file.mediaType });
		}
	}
	return parts;
}

export function ChatContainer({
	id,
	user,
	initialMessages = [],
	onFocus,
	onBlur,
	isExpanded,
	onMessagesChange,
	chatState,
	actionRow,
	onScrollToBottomRef,
	onSuggestClick,
}: ChatContainerProps) {
	const [input, setInput] = useState("");
	const [fileDataUrls, setFileDataUrls] = useState<
		Array<{ file: File; dataUrl: string }>
	>([]);
	const scrollRef = useRef<HTMLDivElement>(null);
	const { getAccessTokenSilently } = useAuth0();
	const { logEvent } = useAnalytics();
	const { pendingMessage, consumeMessage, prefillQuestion, consumePrefill } =
		useChatActions();
	const { t } = useClientTranslations("chat");

	// Format initial messages to match AI SDK UIMessage type
	const formattedInitialMessages: UIMessage[] = initialMessages.map((msg) => {
		// If content is already an array of parts, preserve it (with metadata)
		if (Array.isArray(msg.content)) {
			return {
				id: msg.id,
				role: msg.role as "user" | "assistant",
				parts: msg.content.map((part) => ({
					type: "text" as const,
					text: part.text || "",
					// Preserve custom metadata (e.g., isAmaQuestion)
					...(part as Record<string, unknown>),
				})),
			};
		}

		// Otherwise, create a single text part
		return {
			id: msg.id,
			role: msg.role as "user" | "assistant",
			parts: [
				{
					type: "text" as const,
					text:
						typeof msg.content === "string"
							? msg.content
							: JSON.stringify(msg.content),
				},
			],
		};
	});

	const { messages, sendMessage, status } = useChat({
		id,
		messages: formattedInitialMessages,
		transport: new DefaultChatTransport({
			api: `${import.meta.env.VITE_API_URL || "http://localhost:3001"}/api/chat`,
			credentials: "include",
			headers: async () => {
				const token = await getAccessTokenSilently({
					authorizationParams: {
						audience: import.meta.env.VITE_AUTH0_AUDIENCE,
					},
				});

				return {
					Authorization: `Bearer ${token}`,
				};
			},
			prepareSendMessagesRequest: ({ messages }) => ({
				body: {
					id,
					messages,
				},
			}),
		}),
		onFinish: ({ message }) => {
			scrollToBottom();
			// Track AI response received
			const responseLength =
				message.parts
					?.filter(
						(p): p is { type: "text"; text: string } => p.type === "text",
					)
					.map((p) => p.text || "")
					.join("").length || 0;
			logEvent(ANALYTICS_EVENTS.CHAT_MESSAGE_RECEIVED, undefined, {
				message_length: responseLength,
				chat_id: id,
			});
		},
		onError: (err) => {
			console.error("Chat error:", err);
			toast.error(t("error.messageFailed"));
			logEvent("chat_error", undefined, {
				chat_id: id,
				error_message: err.message,
			});
		},
	});

	// isStreaming: AI is actively sending response chunks
	const isStreaming = status === "streaming";
	// isThinking: request sent, waiting for AI to start responding
	const isThinking = status === "submitted";
	// Combined loading state for UI elements that should be disabled during either
	const isLoading = isStreaming || isThinking;

	// Notify parent when message count changes
	useEffect(() => {
		onMessagesChange?.(messages.length);
	}, [messages.length, onMessagesChange]);

	const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setInput(e.target.value);
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!input.trim() || isLoading) return;

		const userMessage = input.trim();
		setInput("");

		logEvent(ANALYTICS_EVENTS.CHAT_MESSAGE_SENT, undefined, {
			message_length: userMessage.length,
			has_files: fileDataUrls.length > 0,
			file_count: fileDataUrls.length,
		});

		// Upload files to Vercel Blob and get public URLs
		const uploadedFiles =
			fileDataUrls.length > 0
				? await uploadFilesToBlob(fileDataUrls)
				: undefined;

		// Send message with parts
		await sendMessage({
			role: "user",
			parts: buildMessageParts(userMessage, uploadedFiles),
		});

		setFileDataUrls([]);
	};

	const scrollToBottom = useCallback(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		}
	}, []);

	// Expose scrollToBottom to parent via callback ref
	useEffect(() => {
		onScrollToBottomRef?.(scrollToBottom);
	}, [scrollToBottom, onScrollToBottomRef]);

	// Auto-scroll when messages change
	useEffect(() => {
		scrollToBottom();
	}, [scrollToBottom]);

	// Handle pending messages from external triggers (e.g., "Explore with Eve" button)
	useEffect(() => {
		if (pendingMessage && !isLoading) {
			const message = consumeMessage();
			if (message) {
				sendMessage({
					role: "user",
					parts: buildMessageParts(
						message.text,
						message.files,
						message.metadata,
					),
				});
			}
		}
	}, [pendingMessage, isLoading, consumeMessage, sendMessage]);

	// Handle prefill questions from external triggers (e.g., "Ask Eve" button on ContentCard)
	// Unlike pendingMessage, this only populates the input — user must tap send
	// Only apply when input is empty to avoid overwriting user's draft
	useEffect(() => {
		if (prefillQuestion && !isLoading && !input) {
			const text = consumePrefill();
			if (text) {
				setInput(text);
			}
		}
	}, [prefillQuestion, isLoading, input, consumePrefill]);

	const containerStateClass = chatState
		? chatState === "active"
			? styles.activeState
			: chatState === "expandedInput"
				? styles.expandedInputState
				: styles.emptyState
		: messages.length > 0
			? styles.activeState
			: isExpanded
				? styles.expanded
				: styles.emptyState;

	return (
		<div className={`${styles.container} ${containerStateClass}`}>
			<div
				ref={scrollRef}
				className={`${styles.messagesContainer} ${!chatState && isExpanded === false ? styles.collapsed : ""}`}
				data-scrollable="true"
			>
				{messages.length === 0 && !isLoading && (
					<PersonalizedGreeting user={user} />
				)}
				<MessageList
					messages={messages}
					isLoading={isLoading}
					isThinking={isThinking}
					userImage={user.userImage ?? null}
				/>
			</div>

			{actionRow}

			<div className={styles.inputContainer}>
				<ChatInput
					input={input}
					setInput={setInput}
					handleInputChange={handleInputChange}
					handleSubmit={handleSubmit}
					isLoading={isLoading}
					fileDataUrls={fileDataUrls}
					setFileDataUrls={setFileDataUrls}
					onSuggestClick={onSuggestClick}
					onFocus={onFocus}
					onBlur={onBlur}
					isExpanded={isExpanded}
				/>
			</div>
		</div>
	);
}
